import { useMemo, useState, useCallback } from "react";
import * as THREE from "three";
import { useGLTF } from "@react-three/drei";
import { Mesh, Vector3 } from "three";
import {
  InnerModelProcessorResult,
  ProcessedModel,
  InnerModelProcessorConfig,
} from "@/types/laser-engraving";
import { DEFAULT_GLTF_CONFIG, MODEL_PATHS } from "@/lib/laser-physics";

/**
 * 内部模型处理Hook
 *
 * 负责加载和处理激光内雕的内部展示模型，包括：
 * - 模型加载和错误处理
 * - 几何体提取和处理
 * - 整体包围盒计算和缩放
 * - 坐标系统转换
 *
 * @param shouldLoad - 是否应该加载模型
 * @param crystalSize - 水晶容器尺寸，用于计算缩放比例
 * @param config - 模型处理配置
 * @returns 内部模型处理结果
 */
export function useInnerModel(
  shouldLoad: boolean,
  crystalSize: number,
  config: InnerModelProcessorConfig = DEFAULT_GLTF_CONFIG,
): InnerModelProcessorResult {
  // 加载状态管理
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 条件性加载GLTF模型
  const gltf = shouldLoad ? useGLTF(MODEL_PATHS.MICHAEL) : null;

  // 重置错误状态
  const resetError = useCallback(() => {
    setError(null);
  }, []);

  // 🎯 核心处理逻辑 - 提取并处理GLTF模型
  const { processedModels, sourceGeometry } = useMemo(() => {
    if (!gltf) {
      return { processedModels: [], sourceGeometry: null };
    }

    try {
      setIsLoading(true);
      setError(null);

      console.log("🎯 开始处理内部模型（useInnerModel）...");

      // 第一步：从 GLTF 中提取所有Mesh
      let sourceMesh: Mesh = null!;
      const allMeshes: Mesh[] = [];

      gltf.scene.traverse((child) => {
        console.log("遍历GLTF节点:", child.type, child.name);
        if (child instanceof Mesh) {
          const meshChild = child as Mesh;
          console.log(
            "找到Mesh:",
            meshChild.name,
            "顶点数:",
            meshChild.geometry.attributes.position?.count,
          );
          allMeshes.push(meshChild);
          if (!sourceMesh) {
            sourceMesh = meshChild;
          }
        }
      });

      console.log(
        `总共找到 ${allMeshes.length} 个Mesh，使用第一个作为源几何体:`,
        sourceMesh?.name,
      );

      if (!sourceMesh || !sourceMesh.geometry) {
        throw new Error("无法从内部模型中提取几何体");
      }

      // 第二步：计算整体包围盒 - 正确的整体缩放方式
      const tempGroup = new THREE.Group();
      allMeshes.forEach((meshData) => {
        const mesh = new THREE.Mesh(meshData.geometry);
        tempGroup.add(mesh);
      });

      const box = new THREE.Box3().setFromObject(tempGroup);
      const size = new Vector3();
      box.getSize(size);
      const center = new Vector3();
      box.getCenter(center);

      // 第三步：计算统一缩放比例
      const maxDimension = Math.max(size.x, size.y, size.z);
      const targetSize = crystalSize * config.targetSizeRatio;
      const scale = targetSize / maxDimension;

      console.log(
        `Iron Man 整体处理: ${allMeshes.length}个零件, 整体尺寸=${maxDimension.toFixed(2)}, 统一缩放=${scale.toFixed(3)}`,
      );

      // 第四步：生成sourceGeometry用于点云（使用第一个零件）
      const sourceGeometry = sourceMesh.geometry.clone();
      sourceGeometry.scale(scale, scale, scale);
      sourceGeometry.rotateX(config.rotationX);
      if (config.autoCenter) {
        sourceGeometry.center();
      }

      // 第五步：生成用于显示的模型数据 - 整体缩放后保持相对位置
      const processedModels: ProcessedModel[] = allMeshes.map((meshData) => {
        const partGeometry = meshData.geometry.clone();

        // 🎯 关键：只缩放和坐标转换，不单独居中
        partGeometry.scale(scale, scale, scale);
        partGeometry.rotateX(config.rotationX);

        // 🎯 应用整体偏移，使所有零件作为整体居中
        if (config.autoCenter) {
          partGeometry.translate(
            -center.x * scale,
            -center.z * scale,
            center.y * scale,
          );
        }

        return {
          geometry: partGeometry,
          material: meshData.material,
          name: meshData.name,
        };
      });

      setIsLoading(false);
      return { processedModels, sourceGeometry };
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "内部模型处理失败";
      console.error("内部模型处理错误:", errorMessage);
      setError(errorMessage);
      setIsLoading(false);
      return { processedModels: [], sourceGeometry: null };
    }
  }, [
    gltf,
    crystalSize,
    config.targetSizeRatio,
    config.autoCenter,
    config.rotationX,
  ]);

  // 返回处理结果
  return {
    processedModels,
    sourceGeometry,
    isLoading,
    error,
  };
}

// 类型导出，方便其他文件使用
export type {
  InnerModelProcessorResult,
  ProcessedModel,
  InnerModelProcessorConfig,
};
